/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';

import {LangChangeEvent, TranslateService} from '@ngx-translate/core';

import {BehaviorSubject, EMPTY, from, Observable, of} from 'rxjs';
import {debounceTime, distinctUntilChanged, switchMap, takeUntil, tap} from 'rxjs/operators';

import cloneDeep from 'lodash-es/cloneDeep';
import isEqual from 'lodash-es/isEqual';

import {TreeNode} from '@gp/shared/ui';
import {Destroyable} from '../../destroyable.component';
import {CliEmpAttributes} from '../../models/models.interfaces';
import {Data, DataListAndCount} from '../../shared/api';
import {RolesService} from '../../shared/authentication/roles/roles.service';
import {SearchOptions, SelectEmployee} from '../../shared/employees/employees.model';
import {GPTranslateService} from '../../shared/localization/translate.service';
import {ModalComponent, ModalService} from '../../shared/modal';
import {AddFieldsSelection} from '../../shared/ui/add-fields-modal/add-fields-modal.component';
import {TreeItem} from '../../shared/ui/tree-multi-select/treeview.component';
import {User, UserService} from '../../shared/user';
import {getInitials} from '../../shared/utils/strings';
import {PayValueType} from '../elasticquery.interfaces';
import {Report} from '../report';
import {FieldZone, OutputReport, PreviewSettings, ReportingFields, SelectedTreeItem,} from '../reports.interfaces';
import {ReportsService} from '../reports.service';

export const setOutputFiltersPosition = (previewSettings: PreviewSettings):
  { [key: string]: number } => {
  const position: { [key: string]: number } = {};
  const payValueTypes = [PayValueType.PAY_VALUE_SUM, PayValueType.PAY_VALUE_RETRO];
  // When we set field position we increment by 3 because we will let 0 and 1 position for beginDate and
  // endDate and 2 for calculationDate fields
  previewSettings.previewReport.outputFilters.forEach(resultField => {
    position[resultField.field] = (resultField.fieldPosition + 3) * 10;

    // Add another column if we have to display retro and we set his position between the .SUM field position
    // and the next one so we will increment by 5 (ex .SUM position = 30 next .SUM position = 40 so the
    // .Retro will be always in the middle 35, 45
    if (resultField.displayRetro) {
      const resultRetroName = resultField.field + ' - Retroactivity';
      position[resultRetroName] = (resultField.fieldPosition + 3) * 10 + 5;
    }
  });
  if (previewSettings.previewReport.beginDate !== previewSettings.previewReport.endDate ||
    previewSettings.previewReport.outputFilters
      .some(result => result.fieldTypes
        .some(type => payValueTypes.map(value => value.toString())
          .includes(type)))) {
    position['beginDate'] = 0;
    position['endDate'] = 10;
    position['calculationDate'] = 20;
  }
  return position;
};

@Component({
  selector: 'gp-report-top-card',
  templateUrl: './report-top-card.component.html',
  styleUrls: ['./report-top-card.component.scss']
})

export class ReportTopCardComponent extends Destroyable implements OnInit, OnDestroy, OnChanges {

  constructor(public translate: TranslateService,
              private gpTranslateService: GPTranslateService,
              public reportsService: ReportsService,
              public router: Router,
              public modalService: ModalService,
              public formBuilder: UntypedFormBuilder,
              public cd: ChangeDetectorRef,
              private rolesService: RolesService,
              public userService: UserService,
              private route: ActivatedRoute) {
    super();

    this.editReportsForm = this.formBuilder.group({
      title: ['', [
        Validators.required, (control: AbstractControl) => {
          if ((control.value || '').trim()) {
            return null;
          }
          return {
            required: true
          };
        }
      ]],
      activeCheck: '',
      massActionsEnabled: false,
      isStandard: false
    });
  }

  public MAX_FILTER_COUNT = 10;
  public MAX_RESULT_COUNT = 100;
  public MAX_USER_TAGS = 20;

  @ViewChild('templatesSelect', {static: true}) templatesSelect;

  @Input() public report: Report;
  @Input() public reportsTree: TreeNode<TreeItem>[];
  @Input() public user: User;
  @Input() public isPreviewLoaded: boolean = false;
  @Output() public updateReportList: EventEmitter<any> = new EventEmitter<any>();
  @Output() public languageChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() public resetReport: EventEmitter<number> = new EventEmitter<number>();
  @Output() public setEnabledFields: EventEmitter<string[]> = new EventEmitter<string[]>();

  public fields: ReportingFields;
  public currentReportTitle: string;

  public menuOpened: boolean = false;

  public reportModalCancelCallback: () => Promise<void>;
  public reportModalSubmitCallback: () => Promise<void>;

  public fieldsTree: TreeItem[] = [];

  public cliCoreId: number;
  public isEditReport: boolean = true;
  public isPublicReport = false;

  public editReportsForm: UntypedFormGroup;
  public itemMap: Map<string, any> = new Map<string, any>();

  public employeeSelectList: SelectEmployee[] = [];
  public selectedEmployees: Set<number> = new Set<number>();
  public isEmployeesLoading: boolean = false;
  private previousReportId: number;

  public hasWriteRole: boolean = false;
  public enabledFields: string[] = [];
  public opened: boolean;
  public massActionsEnabled: boolean;
  public isStandard: boolean;

  public searchTerm$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public page = 0;
  public perPage = 25;
  public selectedUserIds = new Set<number>();
  public unSelectedUsers: Data<CliEmpAttributes>[] = [];
  public selectedUserTags = [];
  public initialSelectedUserIds = new Set<number>();
  public initialReportEntity = {isPublic: false, editingAllowed: false};

  public reportTitleAlreadyExists = false;
  public reportTitleAlreadyExistsMessage = '';

  protected readonly isEqual = isEqual;
  public hasWriteAccessOnGloReg101 = false;

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalService.modalClosed.emit('create-report');
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.user && this.user) {
      this.cliCoreId = this.user.getCliCore().id;
      this.reportsService.currentFields.subscribe((response: ReportingFields) => {
        if (!response) {
          return;
        }
        this.fields = response;
        this.initFieldsTree(this.fields);
      });
    }
  }

  public ngOnInit(): void {
    this.reportModalCancelCallback = this.reportModalCancel.bind(this);
    this.reportModalSubmitCallback = this.reportModalSubmit.bind(this);
    this.reportsService.isChanged = false;

    this.checkWriteAccessOnRootCliReg();

    this.reportsService.$reportUpdated.subscribe((report: Report) => {
      const selectedReportName = report.reportEntity.title;
      if (selectedReportName?.trim().length) {
        this.templatesSelect.writeValue(selectedReportName);
      }
    });

    this.hasWriteRole = this.rolesService.hasRole('REPORT_WRITE');

    this.gpTranslateService.onLangChange
      .subscribe((langChange: LangChangeEvent) => {
        this.languageChange.next(langChange.lang);
      });

    this.searchTerm$.asObservable()
      .pipe(
        debounceTime(350),
        distinctUntilChanged(),
        switchMap((searchTerm) => searchTerm.length ? this.fetchUsers(searchTerm) : of({items: {data: []}, total: 0})),
        tap((users: DataListAndCount<CliEmpAttributes>) => {
          this.unSelectedUsers = users?.items?.data.filter(user =>
            (this.isUniqueUser(user.id) && !this.isCurrentUser(user.id)));

          this.employeeSelectList = this.generateUserList(this.unSelectedUsers);

          this.isEmployeesLoading = false;
          this.cd.markForCheck();
        })
      ).subscribe();
  }

  public isMyReport(): boolean {
    return this.report.reportEntity.authorId === this.user.id;
  }

// ----------------------------------------Tree multi select-------------------------------------------------------------

  public initFieldsTree(fields: ReportingFields): void {
    this.reportsService.zonesMap = new Map<string, FieldZone>();

    const treeRoot = new TreeItem('root');
    this.fieldsTree = [];
    treeRoot.children = [];
    this.itemMap = new Map<string, any>();

    Object.keys(fields).forEach((key: string) => {
      const item: TreeItem = new TreeItem('', {}, [`REPORTS.${key}_fields`]);
      item.children = [];
      item.value.rootKey = key;
      item.value.path = [];
      item.disabled = fields[key].length === 0;
      this.itemMap.set(key, item);
      treeRoot.children.push(item);
      fields[key].forEach((fieldElement: any) => {
        this.createFamily(key, fieldElement);
      });
    });

    this.fieldsTree = treeRoot.children;
    this.setEnabledFields.emit(this.enabledFields);
    this.cd.markForCheck();
  }

  public createFamily(key: string, fieldElement: any): void {

    if (!fieldElement.transIds) {
      console.warn('A field has been excluded from tree because it misses transIds property.\n', fieldElement);
      return;
    }

    let parent = this.itemMap.get(key);
    const fieldElementKey: string = key + fieldElement.name;

    this.createNode(fieldElementKey, fieldElement.transIds, parent);

    if (fieldElement.items) {

      fieldElement.items.forEach((zonesElement: FieldZone) => {
        zonesElement.groupZoneType = fieldElement.groupZoneType;
        if (zonesElement.items) {
          this.createFamily(fieldElementKey, zonesElement);
        } else {
          const treeItem: TreeItem = new TreeItem('', zonesElement, zonesElement.transIds);
          zonesElement.rootKey = parent.value.rootKey;
          parent = this.itemMap.get(fieldElementKey);
          parent.children.push(treeItem);
          treeItem.value.path = parent.value.path ? parent.value.path.slice() : [];
          treeItem.value.path.push(parent.transIds);
        }

        this.reportsService.zonesMap.set(zonesElement.name, zonesElement);
        this.enabledFields.push(zonesElement.name);
      });
    }
  }

  private createNode(fieldElementKey, transIds: string[], parent: TreeItem) {
    let item = this.itemMap.get(fieldElementKey);
    if (!item) {
      item = new TreeItem('', {}, transIds);
      item.children = [];
      parent.children.push(item);
      item.value.rootKey = parent.value.rootKey;
      item.value.path = parent.value.path ? parent.value.path.slice() : [];
      item.value.path.push(parent.transIds);
      this.itemMap.set(fieldElementKey, item);
    }
  }

  public unCheckedTree() {
    this.fieldsTree.forEach((field: TreeItem) => {
      field.updateCheckedRecursive(false);
      field.updateCollapsedRecursive(true);
    });
  }

// --------------------------------------------------Modal---------------------------------------------------------------
  public openCreateReportModal(): void {
    this.isEditReport = false;
    this.isPublicReport = false;
    this.modalService.openModalWithId('create-report');
    this.setEditReportModalValues();
  }

  public openEditReportModal(): void {
    this.reportTitleAlreadyExists = false;
    this.isEditReport = true;
    this.isPublicReport = true;
    this.menuOpened = false;
    this.currentReportTitle = this.report.reportEntity.title || 'N/A';
    this.modalService.openModalWithId('create-report');
    this.setEditReportModalValues();
  }

  public setEditReportModalValues(): void {
    this.resetTitleExistsValidationMessage('title');

    this.editReportsForm.get('title').setValue(this.report.reportEntity.title);
    this.editReportsForm.get('massActionsEnabled').setValue(this.report.reportEntity.massActionsEnabled);
    if (this.isEditReport) {
      this.editReportsForm.get('isStandard').setValue(this.report.reportEntity.isStandard);
    } else {
      this.editReportsForm.get('isStandard').setValue(false);
    }
  }

  public reportModalCancel(): Promise<void> {
    this.modalService.modalClosed.emit('create-report');
    this.reportTitleAlreadyExists = false;
    return Promise.resolve();
  }

  public reportModalSubmit(): Promise<void> {
    this.reportTitleAlreadyExists = false;

    const outputReport: OutputReport = this.report.getOutputReport();
    const newTitle = this.editReportsForm.get('title').value.trim();
    const titleChanged = newTitle !== this.report.reportEntity.title;

    outputReport.title = newTitle;
    outputReport.massActionsEnabled = this.editReportsForm.get('massActionsEnabled').value;
    outputReport.isStandard = this.editReportsForm.get('isStandard').value;

    if (this.isEditReport) {
      if (titleChanged) {
        this.updateReportWithExistenceCheck(outputReport);
      } else {
        this.reportsService.updateReport(outputReport)
            .pipe(
                tap(() => {
                  this.reportsService.markAsPristine();
                  this.updateReportList.emit();
                  this.modalService.modalClosed.emit('create-report');
                }),
                takeUntil(this.componentDestroyed$)
            ).subscribe();
      }
    } else {
      outputReport.cliCoreId = this.cliCoreId;
      outputReport.authorId = this.user.id;
      outputReport.timeFilter = true;
      outputReport.isPublic = this.isPublicReport;

      this.createReportWithExistenceCheck(outputReport); // For new reports we *always* check
    }

    return Promise.resolve();
  }


  private resetTitleExistsValidationMessage(controlName: string): void {
    this.editReportsForm.get(controlName).valueChanges.pipe(
      tap((value) => {
        if (!value?.length) {
          this.reportTitleAlreadyExists = false;
        }
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  private checkWriteAccessOnRootCliReg(): void {
    this.reportsService.checkWriteAccessOnRootCliReg()
      .pipe(
        tap(cliRegDTO => {
          if (cliRegDTO) {
            this.hasWriteAccessOnGloReg101 = cliRegDTO.write;
          }
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  private createReportWithExistenceCheck(outputReport: OutputReport): void {
    this.reportsService.exists(outputReport)
      .pipe(
        switchMap(exists => {
          if (exists) {
            const title = this.editReportsForm.get('title').value.trim();
            this.reportTitleAlreadyExists = true;
            this.reportTitleAlreadyExistsMessage = outputReport.isStandard ?
              this.translate.instant('REPORTS.standard_report_creation_failed', {title}) :
              this.translate.instant('REPORTS.report_creation_failed', {title});
            this.cd.markForCheck();
            return EMPTY;
          } else {
            this.reportTitleAlreadyExists = false;
            this.cd.markForCheck();
          }
          return this.reportsService.createReport(outputReport);
        }),
        tap(response => {
          if (response) {
            this.reportsService.markAsPristine();
            this.router.navigate(['reports/manage', response.reportTemplateId], { queryParams: { isStandard: outputReport.isStandard } });
            this.editReportsForm.reset();
            this.modalService.modalClosed.emit('create-report');
          }
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  private updateReportWithExistenceCheck(outputReport: OutputReport): void {
    this.reportsService.exists(outputReport)
      .pipe(
        switchMap(exists => {
          if (exists) {
            const title = this.editReportsForm.get('title').value.trim();
            this.reportTitleAlreadyExists = true;
            this.reportTitleAlreadyExistsMessage = outputReport.isStandard ?
              this.translate.instant('REPORTS.standard_report_update_failed', {title}) :
              this.translate.instant('REPORTS.report_update_failed', {title});
            this.cd.markForCheck();
            return EMPTY;
          } else {
            this.reportTitleAlreadyExists = false;
            this.cd.markForCheck();
          }
          return this.reportsService.updateReport(outputReport);
        }),
        tap(() => {
          if (!this.reportTitleAlreadyExists) {
            this.reportsService.markAsPristine();
            this.updateReportList.emit();
            this.modalService.modalClosed.emit('create-report');
          }
        }),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  public getUserLanguage(): string {
    return this.user.language;
  }

  public getCliCoreLanguage(): string {
    return this.user.getCliCore().attributes.language;
  }

  public openModalShare(): void {
    this.menuOpened = false;
    this.modalService.openModalWithId('shareReport');
    this.selectedUserTags = [];
    this.selectedUserIds = new Set<number>();
    this.initialReportEntity = {
      isPublic: this.report.reportEntity.isPublic,
      editingAllowed: this.report.reportEntity.editingAllowed
    };
    Promise.all(this.report.reportEntity.cliUserIds.map(cliUserId => {
      if (cliUserId !== this.user.id) {
        return this.fetchUsers(cliUserId.toString()).pipe(
          tap((users: DataListAndCount<CliEmpAttributes>) => {
            this.unSelectedUsers = users?.items?.data.filter(user =>
              (this.isUniqueUser(user.id) && !this.isCurrentUser(user?.cliEmpId)));
            this.selectedUserTags = [...this.selectedUserTags, ...this.unSelectedUsers
              .filter(user => user.id === cliUserId)
              .map(obj => {
                return {
                  id: obj.id.toString(),
                  name: `${obj?.attributes.lastName.toUpperCase()} ${obj?.attributes.firstName}`
                };
              })];
            this.selectedUserIds.add(cliUserId);
            this.isEmployeesLoading = false;
            this.cd.markForCheck();
          })
        ).toPromise();
      }
      return Promise.resolve();
    })).then(() => {
      this.initialSelectedUserIds = cloneDeep(this.selectedUserIds);
    });
  }

  public closeModal(modal: ModalComponent): void {
    modal.close();
  }

  public shareTemplate(): void {
    this.reportsService.shareTemplate(
      this.report.reportEntity.id,
      Array.from(this.selectedUserTags.map(user => +user.id)),
      this.report.reportEntity.editingAllowed,
      this.report.reportEntity.isPublic
    )
      .subscribe(() => {
        this.updateReportList.emit();
      });
  }

  public selectUser(value: { id: number, value: boolean }): void {
    if (value.value && this.selectedUserIds.size < 20) {
      this.selectedUserIds.add(value.id);
      this.employeeSelectList = this.employeeSelectList.filter(user => !this.selectedUserIds.has(user.id));
      const newUserTag = this.unSelectedUsers
        .filter(user => user.id === value.id)
        .map(obj => {
          return {
            id: obj.id.toString(),
            name: `${obj?.attributes.lastName.toUpperCase()} ${obj?.attributes.firstName}`
          };
        });

      this.selectedUserTags = [...this.selectedUserTags, ...newUserTag];
    } else {
      this.selectedUserIds.delete(value.id);
      this.employeeSelectList = this.employeeSelectList.filter(user => user.id !== value.id);
      this.selectedUserTags = this.selectedUserTags.filter(tag => tag.id !== value.id.toString());
    }
  }

  public removeUser(index: string | number): void {
    const removedUserTagId = this.selectedUserTags[index]?.id;
    this.selectedUserIds.delete(removedUserTagId !== undefined ? +removedUserTagId : Array.from(this.selectedUserIds)[index]);

    const newSelectedUserTags = this.selectedUserTags.filter(user => user.id !== removedUserTagId);
    this.selectedUserTags = [...newSelectedUserTags];
  }


  public generateUserList(userList: Data<CliEmpAttributes>[]): SelectEmployee[] {
    return userList
      .map((element: Data<CliEmpAttributes>) => {
        const isChecked = false;
        return {
          id: element?.id,
          text: this.getSelectedUser(
            `${element?.attributes.lastName.toUpperCase()} ${element?.attributes.firstName}`,
            element?.id?.toString()),
          isChecked
        };
      });
  }

  public getSelectedUser(name: string, status: string): string {
    const parts = name.trim().split(/\s+/);
    const initials = getInitials(parts[0], parts.length > 1 ? parts[parts.length - 1] : '');
    const avatar = `<span class="gp-initials-avatar gp-avatar-md">${initials}</span>`;

    return `<div class="emp">
                <div class="emp-avatar">
                    ${avatar}
                </div>
                <div class="emp-info">
                    <div>${name}</div>
                    <small class="text-muted">${status}</small>
                </div>
            </div>`;
  }

  private isCurrentUser(userId: number): boolean {
    return this.user.id === userId;
  }

  private isUniqueUser(userId: number): boolean {
    return !this.selectedUserIds.has(userId);
  }

  public fetchUsers(searchTerm: string): Observable<DataListAndCount<CliEmpAttributes>> {
    this.isEmployeesLoading = true;
    const options: SearchOptions = {
      search: encodeURIComponent(searchTerm?.trim()),
      page: this.page,
      orderBy: 'lastName',
      perPage: this.perPage
    };
    return this.userService.getAllCliUsers(options);
  }

  public canSaveReport(): boolean {
    const hasFieldsSelected = this.report.columns.length > 0 || this.report.filters.length > 0;
    return hasFieldsSelected && this.reportsService.isChanged;
  }

  public isSaveDisabled(): boolean {
    return this.employeeSelectList.length === 0 && isEqual(this.selectedUserIds, this.initialSelectedUserIds)
      && isEqual(this.report.reportEntity.isPublic, this.initialReportEntity.isPublic)
      && isEqual(this.report.reportEntity.editingAllowed, this.initialReportEntity.editingAllowed);
  }

// -----------------------------------------------Filters----------------------------------------------------------------
  public onFieldsAdded(selection: AddFieldsSelection): void {
    if (selection.target === 'filter' || selection.target === 'both') {
      this.report.addFilters(selection.fields);
    }
    if (selection.target === 'column' || selection.target === 'both') {
      this.report.addColumns(selection.fields);
    }
    this.reportsService.markAsChanged();
  }

// ------------------------------------------------Report----------------------------------------------------------------
  public selectReport(report: SelectedTreeItem): void {

    let reportId ;
    const reportIdWithSuffix = String(report?.id ?? '').split(':');

    let isStandard = false;
    if (reportIdWithSuffix.length > 1) {
      isStandard = reportIdWithSuffix[0] === 'std';
      reportId = reportIdWithSuffix[1] ? +reportIdWithSuffix[1] : 0;
    }else{
      reportId = report.id ? +report.id : 0;
    }

    if (this.report.reportEntity.id === reportId && this.report.reportEntity.isStandard === isStandard) {
      this.canDeactivate().subscribe((canLeave: boolean) => {
        if (canLeave) {
          this.resetReport.emit(reportId);
        }
      });
      return;
    } else if (!reportId) {
      this.router.navigate(['reports/manage']);
      return;
    }
    this.previousReportId = this.report.reportEntity.id;
    this.canDeactivate().subscribe((canLeave: boolean) => {
      if (!canLeave) {
        if (this.templatesSelect && this.report && this.report.reportEntity) {
          this.templatesSelect.writeValue(this.report.reportEntity.title);
        }
        return;
      }
      this.reportsService.entityPathsToDisplay = [];
      this.report.reportEntity.id = reportId;
      this.router.navigate(['reports/manage', reportId], { queryParams: { isStandard } });
    });
  }

  public deleteReport(): void {
    this.reportsService.markAsPristine();
    this.menuOpened = false;
    this.reportsService.deleteReport(this.report.reportEntity.id, this.report.reportEntity.isStandard).subscribe(() => {
      this.router.navigate(['reports/manage']);
    });
  }

  public createNewReport(): void {
    this.canDeactivate().subscribe((canLeave: boolean) => {
      if (!canLeave) {
        return;
      }
      this.report.columns = [];
      this.report.filters = [];
      this.unCheckedTree();
      this.templatesSelect.optionsOpened = false;
      this.router.navigate(['reports/manage']);
    });
  }

  public isReportEditable(): boolean {
    return this.report.reportEntity.id &&
      (this.report.reportEntity.editingAllowed || this.isMyReport()) &&
      this.report.reportEntity.cliUserIds?.includes(this.user.id);
  }

  public isReportStandard(): boolean {
    return this.report.reportEntity.isStandard;
  }

  public isAdminConfigUser(): boolean {
    return this.rolesService.hasRole('CONFIGURATION');
  }

  public fetchUsersByKeyword(keyword: string): void {
    this.searchTerm$.next(keyword);
  }

// ----------------------------------------------Top right menu----------------------------------------------------------

  public save(): void {
    if (this.show3dotsMenuOrUpdateReportModal()) {
      this.modalService.dynamicConfirmModal({
        'title': 'DIALOG.update_report',
        'text': 'DIALOG.update_report?',
        'confirmButton': 'FORMS.update_current_report',
        'cancelButton': 'FORMS.create_new_report',
        'icon': 'file-pen',
        'isCrossButtonVisible': true
      })
        .then((choice: boolean | undefined) => {
          if (choice === true) {
            this.reportsService.updateReport(this.report.getOutputReport()).subscribe(() => {
              this.reportsService.markAsPristine();
              this.updateReportList.emit();
            });
          } else if (choice === false) {
            this.openCreateReportModal();
          }
        });
    } else {
      this.openCreateReportModal();
    }
  }

  public show3dotsMenuOrUpdateReportModal(): boolean {
    return this.isReportEditable() || (this.isReportStandard() && this.hasAccessToStandardReport());
  }

  public hasAccessToStandardReport(): boolean {
    return this.isAdminConfigUser() && this.hasWriteAccessOnGloReg101;
  }

  public canDeactivate(): Observable<boolean> {
    if (!this.reportsService.isChanged) {
      return of(true);
    }
    return from(
      this.modalService.dynamicConfirmModal({
        title: 'DIALOG.you_have_unsaved_changes',
        text: 'DIALOG.you_have_unsaved_changes?',
        confirmButton: 'FORMS.discard_all_changes',
        cancelButton: 'FORMS.keep_editing'
      }).then((choice) => {
        if (choice === true) {
          this.reportsService.markAsPristine();
          return true;
        } else {
          return false;
        }
      })
    );
  }
}

